import Swal from "sweetalert2";
import colors from "assets/theme/base/colors";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */
// gender uses 0 for Male, so check for "empty" explicitly instead of using falsy
export const isEmpty = (value) => value === "" || value === null || value === undefined;

const isBlank = (value) => isEmpty(value) || String(value).trim() === "";

export const labelOf = (name) => name.split("_").join(" ");

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// Keeps digits only, optionally capped at maxLength characters
export const digitsOnly = (value, maxLength) =>
  `${value ?? ""}`.replace(/\D/g, "").slice(0, maxLength);

// Turns what was typed in the devotee search box into devotee form values:
//   "9876543210"          -> { phone }
//   "Ramesh Patel"        -> { first_name, last_name }
//   "Ramesh Kumar Patel"  -> { first_name, middle_name, last_name }
//   "Ramesh"              -> { first_name }
export const parseSearchTerm = (raw = "") => {
  const term = `${raw}`.trim();
  if (!term) return {};

  // Only digits (and common phone separators) -> phone number
  if (/^[\d\s+\-()]+$/.test(term)) {
    return { phone: digitsOnly(term).slice(-10) };
  }

  // Otherwise it is a name: first word = first name, last word = last name,
  // anything in between = middle name
  const parts = term.split(/\s+/);
  const result = { first_name: parts[0] };
  if (parts.length > 1) result.last_name = parts[parts.length - 1];
  if (parts.length > 2) result.middle_name = parts.slice(1, -1).join(" ");
  return result;
};

/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */
// Format rules, checked only when the field has a value
const RULES = {
  phone: {
    isValid: (v) => /^\d{10}$/.test(v),
    message: "Phone number must be exactly 10 digits",
  },
  pincode: {
    isValid: (v) => /^\d{6}$/.test(v),
    message: "Pincode must be exactly 6 digits",
  },
  email: {
    isValid: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    message: "Please enter a valid email address",
  },
  amount: {
    isValid: (v) => Number(v) > 0,
    message: "Amount must be greater than 0",
  },
};

// Returns an error message, or "" when the value is valid
export const getFieldError = (name, value, required = false) => {
  if (required && isBlank(value)) {
    return `${capitalize(labelOf(name))} is required`;
  }

  const rule = RULES[name];
  if (rule && !isEmpty(value) && !rule.isValid(String(value))) {
    return rule.message;
  }

  return "";
};

// fields: [{ name, required }]  ->  { [name]: "error message" | "" }
export const getFormErrors = (fields, data) =>
  Object.fromEntries(
    fields.map(({ name, required }) => [name, getFieldError(name, data?.[name], required)])
  );

export const hasErrors = (errors) => Object.values(errors).some(Boolean);

// Fields that are already invalid when a form is filled in (e.g. an existing record) are
// marked as touched so their errors show straight away. For a brand-new form only fields
// that already hold a value are marked, so an empty form doesn't start out full of errors.
export const getInitialTouched = (errors, data, includeEmpty) =>
  Object.fromEntries(
    Object.keys(errors)
      .filter((name) => errors[name] && (includeEmpty || !isEmpty(data?.[name])))
      .map((name) => [name, true])
  );

/* -------------------------------------------------------------------------- */
/* Confirmation dialog                                                        */
/* -------------------------------------------------------------------------- */
export const confirmAction = async ({ title, text, confirmButtonText = "Yes, proceed!" }) => {
  const result = await Swal.fire({
    title,
    text,
    icon: "question",
    iconColor: "#C89B3C",
    showCancelButton: true,
    confirmButtonColor: "#7A1410",
    cancelButtonColor: "#C89B3C",
    confirmButtonText,
    cancelButtonText: "Cancel",
    didOpen: () => {
      const cancelBtn = Swal.getCancelButton();
      if (cancelBtn) cancelBtn.style.color = "#4A0E0B";
      const titleEl = Swal.getTitle();
      if (titleEl) titleEl.style.color = "#7A1410";
    },
  });
  return result.isConfirmed;
};
